import React from 'react';

import { getEmptyMandatoryOrFieldsWithError, getEmptyNotValidFields, getSortFields, validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { PHONE_BOOK, PHONE_CALCULATOR } from 'apps/auth/components/ga/accounts/send/GaSendConstants';
import SendCommentPopup from 'apps/auth/components/ga/accounts/send/popups/SendCommentPopup/SendCommentPopup';
import PreCheck from 'apps/auth/components/ga/accounts/send/precheck/PreCheck';
import Disclaimer from 'apps/auth/components/send/disclaimers/Disclaimer';
import {
    CALCULATOR_SCREEN,
    SENDER_ADDITIONAL_INFO_SCREEN,
    RECIPIENT_MAIN_INFO_SCREEN,
    CARD_EXTRA_FIELDS_SCREEN,
} from 'apps/auth/components/send/SendConstants';
import ServerErrorPopup from 'apps/auth/components/utils/ServerErrorPopup';
import { DELIVERY_CONTACT, MAX_AMOUNT_ISSUE } from 'apps/auth/constants/Constants';
import { AMOUNT_FROM, AMOUNT_TO, SOURCE_CARD_CVV } from 'apps/auth/constants/fieldsName';
import { CVV, EXP_DATE, PAN } from 'apps/auth/constants/FieldType';
import { GA_SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_GA_POCKET } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { isSoftOrHardServerError } from 'apps/auth/helpers/utils';

import { usePayee } from './payeeServices';

type FieldValue = string | number | boolean | null | undefined;
type FieldError = { code?: string | number; body?: string };
type FormField = {
    name?: string;
    value?: FieldValue;
    visible?: boolean;
    destination?: string;
    type?: string;
    isError?: boolean;
    error?: FieldError;
    [key: string]: unknown;
};
type FieldsMap = Record<string, FormField>;
type ErrorFields = Array<{ name: string; isError?: boolean }> | Record<string, FieldError>;
type ApiErrors = Record<string, FieldError>;
type ApiResponse = {
    messageBodyMessages?: unknown;
    redirectTo?: string;
    showServerError?: boolean;
    errors?: ApiErrors;
    extra?: {
        serverErrorCode?: { code?: string | number };
        textFirstPopup?: string;
        isDisclaimerFromServerSide?: boolean;
        precheck?: unknown;
        payment_form?: { deliveryMethod?: string };
        [key: string]: unknown;
    };
    messages?: { errorText?: string; buttonClose?: string };
};

type ControllerRef = React.MutableRefObject<AbortController | null> | { current?: AbortController | null } | null;
type SubmitRef = React.RefObject<HTMLButtonElement | HTMLInputElement | { click: () => void }>;
type PopupRef = React.RefObject<{ open: (content: React.ReactNode) => void; close: () => void }>;
type PayeeProps = Parameters<typeof usePayee>[4];

type UpdateFormProperties = (formName: string, actionUrl: string | null, submit?: boolean) => Promise<unknown>;
type ChangeFormFieldValue = (formName: string, value: FieldValue, fieldName: string) => void;
type ChangeFormField = (formName: string, field: FormField) => void;
type SetFieldError = (formName: string, fieldName: string, error?: FieldError) => void;
type ScrollToField = (formName: string, fieldName: string) => void;
type ToggleModal = (...args: unknown[]) => void;
type UpdateMessages = (messages: unknown) => void;
type SetScreen = (screen: string) => void;
type SetDisabled = (disabled: boolean) => void;

export const timer = (timerID: ReturnType<typeof setTimeout> | null, callFun: () => void, delay = 300): ReturnType<typeof setTimeout> => {
    if (timerID) {
        clearTimeout(timerID);
    }
    timerID = setTimeout(() => {
        callFun();
    }, delay);
    return timerID;
};

const getNextScreen = (fields: FieldsMap, errorFields: ErrorFields, currentScreen: string): string => {
    const keys: string[] = [];
    if (Array.isArray(errorFields)) {
        errorFields.forEach((f) => {
            if (f.name) {
                keys.push(f.name);
            }
        });
    } else {
        keys.push(...Object.keys(errorFields));
    }
    const amountToName = fields[AMOUNT_TO]?.name;
    const amountFromName = fields[AMOUNT_FROM]?.name;
    const isCalculator = keys.some((k) => k === amountFromName || k === amountToName);
    const hasToFields = keys.some((k) => fields[k]?.destination === 'TO');
    const fieldsExist = keys.some((k) => fields[k]);
    let nextScreen = currentScreen;
    if (fieldsExist && !isCalculator) {
        nextScreen = hasToFields ? RECIPIENT_MAIN_INFO_SCREEN : CARD_EXTRA_FIELDS_SCREEN;
    }
    return nextScreen;
};

const getFromFields = (fields: FieldsMap, showFromFields: boolean, showPaymentFields: boolean): FormField[] => {
    const visibleFields: FormField[] = [];
    (getSortFields(fields) as FormField[]).forEach((field) => {
        const isFromField = field.visible && field.destination === 'FROM';
        const isCardField = field.type === PAN || field.type === CVV || field.type === EXP_DATE;
        if (showFromFields && isFromField && !isCardField) {
            visibleFields.push(field);
        } else if (showPaymentFields && isFromField && isCardField) {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

const getRecipientDetailsFields = (fields: FieldsMap): FormField[] => {
    const visibleFields: FormField[] = [];
    (getSortFields(fields) as FormField[]).forEach((field) => {
        if (field.visible && field.destination === 'TO') {
            visibleFields.push(field);
        }
    });
    return visibleFields;
};

export const hasGaExceedLimitErrors = (fromField: FormField = {}, toField: FormField = {}): boolean => {
    if (fromField?.isError || toField?.isError) {
        const error: Record<string | number, boolean> = {
            [MAX_AMOUNT_ISSUE]: true,
        };
        return Boolean((fromField?.error?.code && error[fromField.error.code]) || (toField?.error?.code && error[toField.error.code]));
    }
    return false;
};

export class GASendMultiScreenService {
    #props: PayeeProps;
    #setScreen: SetScreen;
    #setDisabled: SetDisabled;
    #pocketId: string | number | undefined;

    #controllerRef?: ControllerRef;
    #submitRef?: SubmitRef;
    #popupRef?: PopupRef;

    #updateFormProperties?: UpdateFormProperties;
    #changeFormFieldValue?: ChangeFormFieldValue;
    #changeFormField?: ChangeFormField;
    #setFieldError?: SetFieldError;
    #scrollToField?: ScrollToField;

    #toggleModal?: ToggleModal;
    #updateMessages?: UpdateMessages;

    constructor(props: PayeeProps, setScreen: SetScreen, setDisabled: SetDisabled, pocketId?: string | number) {
        this.#props = props;
        this.#setScreen = setScreen;
        this.#setDisabled = setDisabled;
        this.#pocketId = pocketId;
    }

    setRefs(controllerRef: ControllerRef, submitRef: SubmitRef, popupRef: PopupRef): void {
        this.#controllerRef = controllerRef;
        this.#submitRef = submitRef;
        this.#popupRef = popupRef;
    }

    setFormActions(
        changeFormField: ChangeFormField,
        changeFormFieldValue: ChangeFormFieldValue,
        scrollToField: ScrollToField,
        setFieldError: SetFieldError,
        updateFormProperties: UpdateFormProperties,
    ): void {
        this.#changeFormField = changeFormField;
        this.#changeFormFieldValue = changeFormFieldValue;
        this.#scrollToField = scrollToField;
        this.#setFieldError = setFieldError;
        this.#updateFormProperties = updateFormProperties;
    }

    setAppActions(toggleModal: ToggleModal, updateMessages: UpdateMessages): void {
        this.#toggleModal = toggleModal;
        this.#updateMessages = updateMessages;
    }

    setNextScreen(fields: FieldsMap, currentScreen: string): void {
        let nextScreen = CALCULATOR_SCREEN;
        if (RECIPIENT_MAIN_INFO_SCREEN === currentScreen) {
            nextScreen = CALCULATOR_SCREEN;
        } else if (CALCULATOR_SCREEN === currentScreen && this.#hasEmptySenderFields(fields)) {
            nextScreen = CARD_EXTRA_FIELDS_SCREEN;
        }

        if (nextScreen !== currentScreen) {
            this.#setScreen(nextScreen);
        }
    }

    setNextScreenRateFirstFlow(fields: FieldsMap, currentScreen: string): void {
        let nextScreen = RECIPIENT_MAIN_INFO_SCREEN;

        if (CALCULATOR_SCREEN === currentScreen) {
            nextScreen = RECIPIENT_MAIN_INFO_SCREEN;
        } else if (RECIPIENT_MAIN_INFO_SCREEN === currentScreen && this.#hasEmptySenderFields(fields)) {
            nextScreen = CARD_EXTRA_FIELDS_SCREEN;
        } else if (CARD_EXTRA_FIELDS_SCREEN === currentScreen) {
            nextScreen = CALCULATOR_SCREEN;
        }

        if (nextScreen !== currentScreen) {
            this.#setScreen(nextScreen);
        }
    }

    setErrors(errorFields: Array<{ name: string; isError?: boolean }>): void {
        const errorFieldName = errorFields[0].name;
        for (const f of errorFields) {
            if (f.isError) {
                this.#setFieldError!(GA_SEND_FORM_NAME, f.name);
            }
        }
        this.#scrollToField!(GA_SEND_FORM_NAME, errorFieldName);
    }

    regularBackFlow(currentScreen: string): void {
        if (PHONE_CALCULATOR === currentScreen) {
            this.#setScreen(PHONE_BOOK);
        } else if (CARD_EXTRA_FIELDS_SCREEN === currentScreen) {
            this.#setScreen(CALCULATOR_SCREEN);
        } else if (CALCULATOR_SCREEN === currentScreen) {
            this.#setScreen(RECIPIENT_MAIN_INFO_SCREEN);
        }
    }

    viewRateFirstBackFlow(e: React.SyntheticEvent, currentScreen: string): void {
        if (CALCULATOR_SCREEN !== currentScreen) {
            e.preventDefault();
            if (RECIPIENT_MAIN_INFO_SCREEN === currentScreen) {
                this.#setScreen(CALCULATOR_SCREEN);
            } else if (CARD_EXTRA_FIELDS_SCREEN === currentScreen) {
                this.#setScreen(RECIPIENT_MAIN_INFO_SCREEN);
            }
        }
    }

    regularSubmitHandler(fields: FieldsMap, deliveryMethod: string, messages: Record<string, string>, json: ApiResponse, currentScreen: string): void {
        this.#submitHandler(fields, deliveryMethod, messages, json, currentScreen);
    }

    rateFirstSubmitHandler(fields: FieldsMap, deliveryMethod: string, messages: Record<string, string>, json: ApiResponse, currentScreen: string): void {
        this.#submitHandler(fields, deliveryMethod, messages, json, currentScreen);
    }

    agreeWithDisclaimer(_e: React.SyntheticEvent, submitForm = false): void {
        void this.#updateFormProperties!(GA_SEND_FORM_NAME, `${AUTH_GA_POCKET + this.#pocketId}/send.preCheck?agreeDisclaimer=true`, true).then(() => {
            this.#toggleModal!(false);
            if (submitForm) {
                this.#submitRef?.current?.click();
            }
        });
    }

    toggleContinueButton(fields: FieldsMap, currentScreen: string): void {
        this.#setDisabled(getEmptyMandatoryOrFieldsWithError(this.getVisibleFields(fields, currentScreen), true, false)?.length > 0);
    }

    screenValidation(fields: FieldsMap, _messages: Record<string, string>, _isHighRisk: boolean, switchToNextScreen: () => void, currentScreen: string): void {
        const formName = GA_SEND_FORM_NAME;
        Object.keys(fields).map((key) => {
            const field = fields[key];
            if (field.visible && field.value && typeof field.value === 'string') {
                void validationAndUpdateValue(formName, field.value, field, this.#changeFormField!);
            }
        });
        const apiFields = fields as Record<string, { name: string; canonicalPath?: string | null; value?: string | number | boolean }>;
        void apiFetchData<ApiResponse>(`${AUTH_GA_POCKET + this.#pocketId}/send.preCheck?screenValidation=true`, apiFields, this.#controllerRef).then(
            (json) => {
                if (json?.messageBodyMessages) {
                    this.#updateMessages!(json.messageBodyMessages);
                }
                if (json?.redirectTo) {
                    window.location.href = json.redirectTo;
                } else if (json?.showServerError) {
                    const serverErrorCode = json.extra?.serverErrorCode?.code;
                    const serverErrorValue =
                        typeof serverErrorCode === 'number' ? serverErrorCode : typeof serverErrorCode === 'string' ? Number(serverErrorCode) : undefined;
                    if (!isSoftOrHardServerError(serverErrorValue ?? null)) {
                        this.#popupRef?.current?.open(
                            <ServerErrorPopup popupRef={this.#popupRef} errorText={json.messages?.errorText} buttonClose={json.messages?.buttonClose} />,
                        );
                    }
                } else if (json?.errors) {
                    const errorFields = this.getVisibleFields(fields, currentScreen).filter((f) => json.errors?.[f.name ?? '']);
                    const hasError = errorFields?.length !== 0;
                    if (hasError) {
                        errorFields.forEach((f) => this.#setFieldError!(formName, f.name ?? '', json.errors?.[f.name ?? '']));
                        this.#scrollToField!(formName, errorFields[0].name ?? '');
                    } else if (!hasError && json.extra?.textFirstPopup) {
                        usePayee(json, this.#setScreen, formName, true, this.#props, this.#popupRef);
                    } else {
                        switchToNextScreen();
                    }
                } else {
                    switchToNextScreen();
                }
            },
        );
    }

    getVisibleFields(fields: FieldsMap, currentScreen: string): FormField[] {
        let screenFields: FormField[] = [];
        if (currentScreen === RECIPIENT_MAIN_INFO_SCREEN) {
            screenFields = getRecipientDetailsFields(fields);
        } else if (currentScreen === CALCULATOR_SCREEN) {
            screenFields.push(fields[AMOUNT_FROM]);
            screenFields.push(fields[AMOUNT_TO]);
        } else if (currentScreen === SENDER_ADDITIONAL_INFO_SCREEN) {
            screenFields = getFromFields(fields, true, false);
        } else if (currentScreen === CARD_EXTRA_FIELDS_SCREEN) {
            screenFields = getFromFields(fields, false, true);
        }
        return screenFields;
    }

    #submitHandler(fields: FieldsMap, deliveryMethod: string, messages: Record<string, string>, json: ApiResponse, currentScreen: string): void {
        void this.#updateFormProperties!(GA_SEND_FORM_NAME, `${AUTH_GA_POCKET + this.#pocketId}/send.preCheck`, true);
        if (json) {
            if (json.errors && Object.keys(json.errors).length) {
                this.#showScreenWithErrors(fields, json.errors, currentScreen);
                this.#scrollToField!(GA_SEND_FORM_NAME, Object.keys(json.errors)[0]);
            } else if (json.extra?.isDisclaimerFromServerSide) {
                this.#toggleModal!(
                    true,
                    <Disclaimer
                        formName={GA_SEND_FORM_NAME}
                        clickAgree={(e: React.SyntheticEvent) => {
                            this.agreeWithDisclaimer(e, true);
                        }}
                    />,
                    true,
                    false,
                );
            } else if (deliveryMethod === DELIVERY_CONTACT && currentScreen === PHONE_BOOK) {
                this.#setScreen(PHONE_CALCULATOR);
            } else if (json.extra?.precheck && json.extra?.payment_form?.deliveryMethod === DELIVERY_CONTACT) {
                void this.#updateFormProperties!(GA_SEND_FORM_NAME, null, true).then(() => {
                    this.#popupRef?.current?.open(<SendCommentPopup popupRef={this.#popupRef} formName={GA_SEND_FORM_NAME} />);
                });
            } else if (json.extra?.precheck) {
                this.#popupRef?.current?.open(<PreCheck popupRef={this.#popupRef} formName={GA_SEND_FORM_NAME} />);
            }
        }
    }

    #hasEmptySenderFields(fields: FieldsMap = {}): boolean {
        return (
            getEmptyNotValidFields(
                Object.values(fields).filter((f) => 'FROM' === f?.destination),
                false,
            )?.length > 0
        );
    }

    #showScreenWithErrors(fields: FieldsMap, errorFields: ApiErrors, currentScreen: string): void {
        this.#popupRef?.current?.close();
        if (Object.values(fields).some((f) => CVV === f?.type)) {
            this.#changeFormFieldValue!(GA_SEND_FORM_NAME, null, SOURCE_CARD_CVV);
        }
        this.setNextScreen(fields, currentScreen);
        const nextScreen = getNextScreen(fields, errorFields, currentScreen);
        if (nextScreen !== currentScreen) {
            this.#setScreen(nextScreen);
        }
    }
}
